import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { EventType, InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import { LandingPage } from './Routes/LandingPage';
import { CandidatePortal } from './Routes/CandidatePortal';
import { Dashboard } from './Routes/CandidatePages/Dashboard';
import { ErrorPage } from './Routes/errorpages/ErrorPage';
import {
  LandingContent,
  Job,
  Jobs,
  Tifo,
  TimeAndExpense,
  ProfileBuilderOne,
  ProfileBuilderTwo,
  ProfileBuilderThree,
  ProfileBuilderFour,
  ProfileBuilderFive,
  ProfileBuilderSix,
  ProfileBuilderSeven,
  MyProfile
} from './Routes';
import { backendAuth, backendUser } from './Api';
import ProtectedRoute from './Components/ProtectedRoute';
import './scss/App.scss';
import RightToBeForgotten from './Routes/rightToBeForgotten/RightToBeForgotten';
import configData from './config.json';
import SEO from './Components/common/SEO/SEO';
import { fetchUserData, initUserData } from './actions/user';
import { checkOnboardingCookie } from './utils/helpers';
import api from './api/apiSettings';
import { getConstants } from './actions/constFromApi';
import { handleLogin } from './utils/loginFunctions';

const backendAuthorization = (token, navigateRoute, navigate) =>
  backendAuth()
    .then((authResponse) => {
      const {
        status: authStatus,
        data: {
          Data: { FirstName, Id, IsFirstLogin }
        }
      } = authResponse;
      if (authStatus === 200) {
        localStorage.setItem('AirswiftFirstName', FirstName);
        localStorage.setItem('UserId', Id);
      }
      return { token, Id, navigateRoute, navigate, IsFirstLogin };
    })
    .catch((error) => console.log(error));

const getUserInfo = ({ Id, navigateRoute, navigate, IsFirstLogin, dispatch }) =>
  backendUser(Id)
    .then((userResponse) => {
      const {
        status: userStatus,
        data: {
          Data: { IsProfileCompleted }
        }
      } = userResponse;
      if (userStatus === 200) {
        const jobForSaving = localStorage.getItem('saveJobAfterLogin');
        if (jobForSaving) {
          api.post(`v1/job/save-job`, { JobId: JSON.parse(jobForSaving), UserId: Id });
          localStorage.removeItem('saveJobAfterLogin');
        }
        dispatch(initUserData(userResponse?.data?.Data));
        localStorage.setItem('userProfileCompleted', IsProfileCompleted);
        if (navigateRoute) {
          navigate(navigateRoute);
        } else if (IsFirstLogin && !IsProfileCompleted && !checkOnboardingCookie())
          navigate('/dashboard/profile-builder-one');
      }
    })
    .catch((error) => console.log(error));

function App({ msalInstance }) {
  return (
    <Router>
      <AppComponent msalInstance={msalInstance} />
    </Router>
  );
}

function AppComponent({ msalInstance }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useIsAuthenticated();
  const { inProgress, instance } = useMsal();
  const userProfile = useSelector((state) => state.user?.profile);
  const [prevProgress, setPrevProgress] = useState(null);

  msalInstance.addEventCallback((message) => {
    if (localStorage.getItem('forRestoreStore')) localStorage.removeItem('forRestoreStore');

    if (
      (message.eventType === EventType.LOGIN_SUCCESS && message.payload.accessToken) ||
      (message.eventType === EventType.HANDLE_REDIRECT_END && window.location.hash.includes('access_token'))
    ) {
      let accessToken;
      let state = '';

      if (message.eventType === EventType.LOGIN_SUCCESS) {
        accessToken = message.payload.accessToken;
        state = message.payload.state;
      }
      if (message.eventType === EventType.HANDLE_REDIRECT_END) {
        // eslint-disable-next-line prefer-destructuring
        accessToken = window.location.hash.split('=')[1];
      }

      const navigateRoute = JSON.parse(state || '{}')?.navigate;
      localStorage.setItem('AirswiftAccessToken', accessToken);

      backendAuthorization(accessToken, navigateRoute, navigate).then((res) => getUserInfo({ ...res, dispatch }));
    }
  });

  const fetchConstants = () => {
    dispatch(getConstants());
  };

  useEffect(() => {
    if (
      window.location.pathname.toLowerCase().includes('dashboard') &&
      !isAuthenticated &&
      inProgress === InteractionStatus.None &&
      prevProgress === InteractionStatus.Startup &&
      !window.location.hash.includes('access_token')
    ) {
      if (window.location.pathname.includes('/dashboard/')) {
        navigate(window.location.pathname.replace('/dashboard/', '') + window.location.search);
      } else {
        handleLogin(instance);
      }
    }
    setPrevProgress(inProgress);
    const userId = localStorage.getItem('UserId');
    if (!userProfile && userId && isAuthenticated) dispatch(fetchUserData(userId));
  }, [isAuthenticated, inProgress]);

  useEffect(() => {
    fetchConstants();
  }, []);

  return (
    <div className="App">
      <div id="root-portal" />
      <SEO
        title="The best talent deserves the best tech"
        description="Welcome to iContract, Airswift's self-service portal designed
        to simplify the employment process from start to finish."
        name="Airswift"
        type="website"
        imageUrl={`${configData.REACT_APP_PUBLIC_URL}AirswiftLogo600x600.png`}
        contentUrl={`${configData.REACT_APP_PUBLIC_URL}${window.location.pathname}`}
      />

      <Routes>
        <Route path="/" element={<LandingPage />}>
          <Route index element={<LandingContent />} />
          <Route path="jobs">
            <Route
              path=""
              element={
                <Jobs msalInstance={msalInstance} isLoggedIn={isAuthenticated} navigate={(link) => navigate(link)} />
              }
            />
          </Route>
          <Route path="jobs/:id" element={<Job />} />
        </Route>
        <Route
          path="my-profile"
          element={
            <ProtectedRoute>
              <CandidatePortal />
            </ProtectedRoute>
          }
        >
          <Route
            path=""
            element={
              JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <MyProfile />
              ) : (
                <Navigate to="/dashboard/profile-builder-one" />
              )
            }
          />
          <Route path="rtbf" element={<RightToBeForgotten />} />
        </Route>
        <Route
          path="dashboard"
          element={
            isAuthenticated && (
              <ProtectedRoute>
                <CandidatePortal />
              </ProtectedRoute>
            )
          }
        >
          <Route path="" element={<Dashboard />} />
          <Route path="jobs">
            <Route
              path=""
              element={
                <Jobs msalInstance={msalInstance} isLoggedIn={isAuthenticated} navigate={(link) => navigate(link)} />
              }
            />
          </Route>
          <Route path="jobs/:id" element={<Job />} />
          <Route path="tifo" element={checkOnboardingCookie() ? <Tifo /> : <Navigate to="/dashboard" />} />
          <Route path="time-and-expense" element={<TimeAndExpense />} />
          <Route
            path="profile-builder-one"
            element={
              !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <ProfileBuilderOne />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="profile-builder-two"
            element={
              !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <ProfileBuilderTwo />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="profile-builder-three"
            element={
              !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <ProfileBuilderThree />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="profile-builder-four"
            element={
              !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <ProfileBuilderFour />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="profile-builder-five"
            element={
              !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <ProfileBuilderFive />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="profile-builder-six"
            element={
              !JSON.parse(localStorage.getItem('userProfileCompleted')) ? (
                <ProfileBuilderSix />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route path="profile-builder-seven" element={<ProfileBuilderSeven />} />
        </Route>
        <Route path="/500" element={<ErrorPage />} />
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </div>
  );
}

AppComponent.propTypes = {
  msalInstance: PropTypes.any.isRequired
};

export default App;
